import { useCallback, useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import constant from 'lodash/constant'
import { ROUTE_COLLECT, ROUTE_COLLECTIONS } from '@klear/constants'
import type { Money } from '@klear/money'
import type { WizardStepGroupNavState } from '@klear/ui/wizard/nav-state'
import { bindWizardSteps, type WizardStepModule } from '@klear/ui/wizard/flow'
import { WizardFlowContent } from '@klear/ui/wizard/flow-content'
import { usePathSyncedWizardFlow } from '@klear/ui/wizard/use-step-group-nav'
import {
  COLLECTION_STEP_ID,
  COLLECTION_STEP_LABELS,
  COLLECTION_TYPE,
  COLLECTION_TYPE_LABELS,
  type CollectionStepId
} from '@/app/collections/steps'
import {
  useCollectionOutletContext,
  type CollectionOutletContext,
  type CollectionStepCtx
} from '@/app/collections/outlet-context'
import SelectCollectionTypeStep from '@/app/collections/steps/SelectCollectionTypeStep'
import SelectTraderStep from '@/app/collections/steps/SelectTraderStep'
import SelectItemsStep from '@/app/collections/steps/SelectItemsStep'
import RecalculateAllocationStep from '@/app/collections/steps/RecalculateAllocationStep'
import ReviewStep from '@/app/collections/steps/ReviewStep'

function isWithinRemaining(money: Money, remaining: Money): boolean {
  if (!money.isPositive()) return false
  if (money.currency !== remaining.currency) return false
  return money.lessThanOrEqualTo(remaining)
}

export function isSelectItemsComplete(outlet: CollectionOutletContext): boolean {
  const { state, collectableFeeRows, collectableRecoveries } = outlet
  if (state.collectionType === COLLECTION_TYPE.FEES) {
    if (state.selectedFees.length === 0) return false
    return state.selectedFees.every((sel) => {
      const row = collectableFeeRows.find((r) => r.fee.id === sel.feeId)
      if (!row) return false
      return isWithinRemaining(sel.money, row.remainingAmount)
    })
  }
  if (state.collectionType === COLLECTION_TYPE.ADVANCE_RECOVERY) {
    if (state.selectedRecoveries.length === 0) return false
    return state.selectedRecoveries.every((sel) => {
      const recovery = collectableRecoveries.find((r) => r.id === sel.orderEventId)
      if (!recovery) return false
      return isWithinRemaining(sel.money, recovery.remainingAmount)
    })
  }
  return false
}

function canCollect(outlet: CollectionOutletContext): boolean {
  const { state } = outlet
  return (
    (state.collectionType === COLLECTION_TYPE.FEES && state.selectedFees.length > 0) ||
    (state.collectionType === COLLECTION_TYPE.ADVANCE_RECOVERY && state.selectedRecoveries.length > 0)
  )
}

const selectTypeModule: WizardStepModule<CollectionStepCtx> = {
  id: COLLECTION_STEP_ID.SELECT_COLLECTION_TYPE,
  label: COLLECTION_STEP_LABELS[COLLECTION_STEP_ID.SELECT_COLLECTION_TYPE],
  isComplete: (ctx) => ctx.state.collectionType != null,
  component: SelectCollectionTypeStep
}

const selectTraderModule: WizardStepModule<CollectionStepCtx> = {
  id: COLLECTION_STEP_ID.SELECT_TRADER,
  label: COLLECTION_STEP_LABELS[COLLECTION_STEP_ID.SELECT_TRADER],
  isComplete: (ctx) => ctx.state.traderId != null,
  component: SelectTraderStep
}

const selectItemsModule: WizardStepModule<CollectionStepCtx> = {
  id: COLLECTION_STEP_ID.SELECT_ITEMS,
  label: COLLECTION_STEP_LABELS[COLLECTION_STEP_ID.SELECT_ITEMS],
  isComplete: (ctx) => isSelectItemsComplete(ctx),
  component: SelectItemsStep
}

const recalculateAllocationModule: WizardStepModule<CollectionStepCtx> = {
  id: COLLECTION_STEP_ID.RECALCULATE_ALLOCATION,
  label: COLLECTION_STEP_LABELS[COLLECTION_STEP_ID.RECALCULATE_ALLOCATION],
  visible: (ctx) => ctx.state.collectionType === COLLECTION_TYPE.ADVANCE_RECOVERY && ctx.isEarlyAdvanceRecovery,
  isComplete: constant(true),
  component: RecalculateAllocationStep
}

const reviewModule: WizardStepModule<CollectionStepCtx> = {
  id: COLLECTION_STEP_ID.REVIEW,
  label: COLLECTION_STEP_LABELS[COLLECTION_STEP_ID.REVIEW],
  isComplete: constant(false),
  component: ReviewStep
}

const COLLECTION_STEP_MODULES: Array<WizardStepModule<CollectionStepCtx>> = [
  selectTypeModule,
  selectTraderModule,
  selectItemsModule,
  recalculateAllocationModule,
  reviewModule
]

function resolveInitialStepId(stepIdParam: string | undefined, outlet: CollectionOutletContext): CollectionStepId {
  const firstStep = outlet.stepOrder[0] ?? COLLECTION_STEP_ID.SELECT_COLLECTION_TYPE
  if (!stepIdParam || !outlet.stepOrder.includes(stepIdParam as CollectionStepId)) return firstStep
  const requested = stepIdParam as CollectionStepId
  if (requested === firstStep) return requested
  if (!outlet.state.collectionType) return firstStep
  const needsTrader =
    requested === COLLECTION_STEP_ID.SELECT_ITEMS ||
    requested === COLLECTION_STEP_ID.RECALCULATE_ALLOCATION ||
    requested === COLLECTION_STEP_ID.REVIEW
  if (needsTrader && outlet.state.traderId == null) return firstStep
  return requested
}

export type CollectionFlowProps = {
  onNavStateChange: (nav: WizardStepGroupNavState) => void
}

export function CollectionFlow({ onNavStateChange }: CollectionFlowProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { stepId: stepIdParam } = useParams<{ stepId?: string }>()
  const outlet = useCollectionOutletContext()

  const stepDefs = useMemo(() => bindWizardSteps(COLLECTION_STEP_MODULES, outlet), [outlet])

  const syncPathStepId = useCallback(
    (stepId: string) => {
      const qs = searchParams.toString()
      const suffix = qs.length > 0 ? `?${qs}` : ''
      navigate(`/${ROUTE_COLLECTIONS}/${ROUTE_COLLECT}/${stepId}${suffix}`, { replace: true })
    },
    [navigate, searchParams]
  )

  const flow = usePathSyncedWizardFlow(stepDefs, {
    pathStepId: stepIdParam,
    resolveInitialStepId: (pathStepId) => resolveInitialStepId(pathStepId, outlet),
    syncPathStepId,
    stepSelection: 'gated',
    onNavStateChange,
    onExitFirstStep: () => navigate(`/${ROUTE_COLLECTIONS}`)
  })

  const collectionTypeLabel = outlet.state.collectionType
    ? COLLECTION_TYPE_LABELS[outlet.state.collectionType].toLowerCase()
    : 'collection'
  const submitLabel = outlet.isCollecting ? 'Collecting…' : `Collect ${collectionTypeLabel}`

  return (
    <WizardFlowContent
      flow={flow}
      className="w-full max-w-6xl space-y-4"
      onSubmit={() => void outlet.collect()}
      cancelOnFirstStep
      disabled={outlet.isCollecting}
      submitDisabled={!canCollect(outlet) || outlet.isCollecting}
      submitLabel={submitLabel}
      navClassName="flex justify-between gap-2 pt-4"
    />
  )
}
